/**
 * Utility for caching flag images to improve performance
 * This helps reduce redundant requests for flag SVGs that are displayed multiple times
 */

// In-memory cache that persists during the session
interface FlagCache {
  svgs: Map<string, string>;
  loadStatus: Map<string, 'loading' | 'loaded' | 'error'>;
  callbacks: Map<string, Array<() => void>>;
}

// Initialize the cache
const flagCache: FlagCache = {
  svgs: new Map<string, string>(),
  loadStatus: new Map<string, 'loading' | 'loaded' | 'error'>(),
  callbacks: new Map<string, Array<() => void>>()
};

/**
 * Get a flag SVG from cache if available, otherwise fetch and cache it
 * @param countryCode ISO 3166-1 alpha-2 country code (e.g., 'US', 'GB')
 * @param onLoad Callback to execute when the image is loaded
 * @param onError Callback to execute if the image fails to load
 * @returns The cached image URL or undefined if not cached yet
 */
export const getCachedFlagSvg = (
  countryCode: string,
  onLoad: () => void,
  onError: () => void
): string | undefined => {
  const normalizedCode = countryCode.toLowerCase();
  
  // If we already have the SVG cached, return it immediately
  if (flagCache.svgs.has(normalizedCode)) {
    // Still call onLoad to ensure consistency
    setTimeout(onLoad, 0);
    return flagCache.svgs.get(normalizedCode);
  }
  
  // If it's currently loading, register the callbacks for when it finishes
  if (flagCache.loadStatus.get(normalizedCode) === 'loading') {
    const callbacks = flagCache.callbacks.get(normalizedCode) || [];
    callbacks.push(onLoad);
    flagCache.callbacks.set(normalizedCode, callbacks);
    return undefined;
  }
  
  // If it previously failed to load, call the error callback and return undefined
  if (flagCache.loadStatus.get(normalizedCode) === 'error') {
    setTimeout(onError, 0);
    return undefined;
  }
  
  // Generate the flag URL
  const flagUrl = `https://flagcdn.com/w20/${normalizedCode}.png`;
  
  // Start loading the flag
  flagCache.loadStatus.set(normalizedCode, 'loading');
  flagCache.callbacks.set(normalizedCode, [onLoad]);
  
  const img = new Image();
  img.onload = () => {
    // Cache the successful result
    flagCache.svgs.set(normalizedCode, flagUrl);
    flagCache.loadStatus.set(normalizedCode, 'loaded');
    
    // Execute all waiting callbacks
    const callbacks = flagCache.callbacks.get(normalizedCode) || [];
    callbacks.forEach(callback => callback());
    flagCache.callbacks.delete(normalizedCode);
  };
  
  img.onerror = () => {
    // Mark as error in the cache
    flagCache.loadStatus.set(normalizedCode, 'error');
    
    // Execute the error callback
    onError();
    
    // Log the error for debugging (only in development)
    if (process.env.NODE_ENV === 'development') {
      console.warn(`Failed to load flag for country code: ${countryCode}`);
    }
    
    // Clear any waiting callbacks
    flagCache.callbacks.delete(normalizedCode);
  };
  
  img.src = flagUrl;
  return undefined;
};

/**
 * Check if a flag is already cached
 * @param countryCode ISO 3166-1 alpha-2 country code
 * @returns Boolean indicating if the flag is cached
 */
export const isFlagCached = (countryCode: string): boolean => {
  const normalizedCode = countryCode.toLowerCase();
  return flagCache.svgs.has(normalizedCode) || flagCache.loadStatus.get(normalizedCode) === 'loaded';
};

/**
 * Preload a list of country flags for future use
 * Useful for prefetching flags that will be displayed later in the application
 * @param countryCodes Array of ISO 3166-1 alpha-2 country codes
 */
export const preloadFlags = (countryCodes: string[]): void => {
  countryCodes.forEach(countryCode => {
    const normalizedCode = countryCode.toLowerCase();
    
    // Skip if already cached or loading
    if (flagCache.loadStatus.has(normalizedCode)) return;
    
    // Start loading the flag
    const flagUrl = `https://flagcdn.com/w20/${normalizedCode}.png`;
    flagCache.loadStatus.set(normalizedCode, 'loading');
    
    const img = new Image();
    img.onload = () => {
      flagCache.svgs.set(normalizedCode, flagUrl);
      flagCache.loadStatus.set(normalizedCode, 'loaded');
    };
    
    img.onerror = () => {
      flagCache.loadStatus.set(normalizedCode, 'error');
      console.warn(`Failed to preload flag for country code: ${countryCode}`);
    };
    
    img.src = flagUrl;
  });
};

/**
 * Clear the flag cache
 * Useful for managing memory in long-running applications
 */
export const clearFlagCache = (): void => {
  flagCache.svgs.clear();
  flagCache.loadStatus.clear();
  flagCache.callbacks.clear();
};

export default flagCache;
